import { supabase } from '../lib/supabase';
import { RecommendedVideo } from '../components/VideoRecommendationCard';

export interface ChatMessage {
  id: string;
  session_id: string;
  sender: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: {
    recommended_videos?: RecommendedVideo[];
    [key: string]: any;
  };
  created_at: string;
}

const GUEST_TOKEN_KEY = 'masterclub_chat_guest_token';
const PHONE_KEY = 'masterclub_chat_phone';

// Obtém ou gera um token de visitante único
export function getOrCreateGuestToken(): string {
  let token = localStorage.getItem(GUEST_TOKEN_KEY);
  if (!token) {
    token = 'guest_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem(GUEST_TOKEN_KEY, token);
  }
  return token;
}

// Salva/obtém telefone para identificação sem login
export function getStoredPhoneNumber(): string | null {
  return localStorage.getItem(PHONE_KEY);
}

export function storePhoneNumber(phone: string): void {
  localStorage.setItem(PHONE_KEY, phone);
}

// Obtém ou cria uma sessão de chat no Supabase (utiliza RPC get_or_create_chat_session)
export async function getOrCreateChatSession(
  userId?: string | null,
  phoneNumber?: string | null
): Promise<string | null> {
  try {
    const guestToken = getOrCreateGuestToken();
    const phoneToUse = phoneNumber || getStoredPhoneNumber();

    const { data, error } = await supabase.rpc('get_or_create_chat_session', {
      p_user_id: userId || null,
      p_phone_number: phoneToUse || null,
      p_guest_token: guestToken,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao obter/criar sessão de chat:', error);
    return null;
  }
}

// Carrega as mensagens de uma sessão
export async function loadChatMessages(sessionId: string): Promise<ChatMessage[]> {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao carregar mensagens:', error);
    return [];
  }
}

// Insere uma mensagem na tabela chat_messages
export async function sendChatMessage(
  sessionId: string,
  sender: 'user' | 'assistant' | 'system',
  content: string,
  metadata: Record<string, any> = {}
): Promise<ChatMessage | null> {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        session_id: sessionId,
        sender,
        content,
        metadata,
      })
      .select()
      .single();

    if (error) throw error;

    // Atualiza o last_active_at da sessão
    await supabase
      .from('chat_sessions')
      .update({ last_active_at: new Date().toISOString() })
      .eq('id', sessionId);

    return data;
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    return null;
  }
}

// Opcional: dispara a mensagem para a webhook do n8n se configurada no .env ou via Netlify Function
export async function triggerN8nWorkflow(payload: {
  sessionId: string;
  message: string;
  userId?: string | null;
  phoneNumber?: string | null;
}): Promise<boolean> {
  const n8nWebhookUrl = (import.meta as any).env?.VITE_N8N_CHAT_WEBHOOK_URL;
  if (!n8nWebhookUrl) return false;

  try {
    await fetch(n8nWebhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return true;
  } catch (err) {
    console.warn('Alerta ao disparar n8n webhook:', err);
    return false;
  }
}
